<script setup lang="ts">
import { computed, onMounted, onUnmounted, ref } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useTheme } from 'vuetify'
import { useRegisterSW } from 'virtual:pwa-register/vue'
import {
  activateWaitingWorker,
  createUpdateRecorder,
  safeSwUpdate,
  takeRecentUpdateAttempt,
} from '@/utils/swUpdate'
import { reportClientDiagnostic } from '@/composables/useErrorReporter'
import { useAppStore } from '@/stores/app'
import { useAuthStore } from '@/stores/auth'
import { useDepartmentStore } from '@/stores/department'
import { useStrategyStore } from '@/stores/strategy'
import { peopleApi, timeTrackingApi } from '@/plugins/api'
import { usePushNotifications } from '@/composables/usePushNotifications'
import ActivityFeedBell from '@/components/ActivityFeedBell.vue'
import ActivityFeedPanel from '@/components/ActivityFeedPanel.vue'
import GlobalFAB from '@/components/GlobalFAB.vue'
import ModalAlertDialog from '@/components/ModalAlertDialog.vue'
import ImpersonationBanner from '@/components/ImpersonationBanner.vue'
import DepartmentSwitcher from '@/components/DepartmentSwitcher.vue'

const theme = useTheme()
const appStore = useAppStore()
const authStore = useAuthStore()
// «Мои отчёты» — те же роли, что на бэкенде (editor+); viewer их не видит
const canSeeMyReports = computed(() =>
  ['editor', 'executive', 'admin', 'superadmin'].some((r) => authStore.roles.includes(r)),
)
const departmentStore = useDepartmentStore()
const strategyStore = useStrategyStore()
const pendingClassifications = computed(() =>
  (strategyStore.classificationStats.pending ?? 0) +
  (strategyStore.classificationStats.diverged ?? 0)
)
const { isSupported: pushSupported, isEnabled: pushEnabled, canEnable: canEnablePush, isCurrentDeviceSubscribed, subscribe: subscribeToPush } = usePushNotifications()

const route = useRoute()
const router = useRouter()
const isPublicRoute = computed(() => route.meta.public === true || window.location.pathname.startsWith('/public/'))
const isDark = computed(() => theme.global.current.value.dark)

// Time tracking admin access (superadmin OR has subordinates)
const hasTimeTrackingAdmin = ref(false)

// PWA Update state
const showUpdateSnackbar = ref(false)

// Push notifications prompt state
const showPushPrompt = ref(false)
const PUSH_PROMPT_COOLDOWN_DAYS = 7
const PUSH_PROMPT_STORAGE_KEY = 'push_notification_prompt_dismissed_at'

// Check if we should show push notification prompt
function shouldShowPushPrompt(): boolean {
  // Check cooldown
  const dismissedAt = localStorage.getItem(PUSH_PROMPT_STORAGE_KEY)
  if (dismissedAt) {
    const daysSinceDismiss = (Date.now() - parseInt(dismissedAt, 10)) / (1000 * 60 * 60 * 24)
    if (daysSinceDismiss < PUSH_PROMPT_COOLDOWN_DAYS) {
      return false
    }
  }

  // Show prompt if:
  // - Push is supported and enabled on server
  // - User can enable push (not denied)
  // - Current device is not subscribed
  return Boolean(pushSupported.value && pushEnabled.value && canEnablePush.value && !isCurrentDeviceSubscribed.value)
}

// Lazy check for push notifications (non-blocking)
function checkPushNotifications() {
  // Delay check to not block initial render
  setTimeout(() => {
    if (shouldShowPushPrompt()) {
      showPushPrompt.value = true
    }
  }, 5000) // 5 second delay
}

// Handle push prompt actions
function dismissPushPrompt() {
  showPushPrompt.value = false
  localStorage.setItem(PUSH_PROMPT_STORAGE_KEY, Date.now().toString())
}

async function enablePushNotifications() {
  showPushPrompt.value = false
  const success = await subscribeToPush()
  if (!success) {
    // If failed, don't set cooldown so user can try again
    console.warn('[App] Failed to enable push notifications')
  }
}

// Попытка обновления из прошлой загрузки вкладки (если «Обновить» нажимали
// только что) — нужна onNeedRefresh для диагностики
let lastUpdateAttempt = takeRecentUpdateAttempt()

// Register Service Worker with prompt for update
useRegisterSW({
  immediate: true,
  onRegisteredSW(swUrl, registration) {
    console.log('[App] Service Worker registered:', swUrl)
    // Check for updates every 5 minutes
    // (safeSwUpdate пропускает переходные состояния регистрации и гасит
    // InvalidStateError; прочие ошибки уходят в журнал как раньше)
    if (registration) {
      setInterval(() => {
        void safeSwUpdate(registration)
      }, 5 * 60 * 1000)
    }
  },
  onRegisterError(error) {
    console.error('[App] Service Worker registration failed:', error)
  },
  onNeedRefresh() {
    console.log('[App] New content available, showing update prompt')
    // Плашка вернулась сразу после «Обновить» — новый воркер не взял
    // управление (Safari, 14.10). Шаги попытки — в журнал ошибок дашборда
    if (lastUpdateAttempt) {
      reportClientDiagnostic(
        'pwa-update',
        `После «Обновить» плашка вернулась: ${lastUpdateAttempt.steps.join(' ')} | ${navigator.userAgent}`,
      )
      lastUpdateAttempt = null
    }
    showUpdateSnackbar.value = true
  },
  onOfflineReady() {
    console.log('[App] App ready to work offline')
  },
})

// Listen for SW activation message
if ('serviceWorker' in navigator) {
  navigator.serviceWorker.addEventListener('message', (event) => {
    if (event.data?.type === 'SW_ACTIVATED') {
      console.log('[App] New Service Worker activated, version:', event.data.version)
      // Перезагрузку делает applyUpdate() после подтверждения пользователем
    }
  })
}

// Применить обновление: SKIP_WAITING ожидающему воркеру напрямую и
// перезагрузка (по смене контроллера или по таймауту) — см. activateWaitingWorker
function applyUpdate() {
  showUpdateSnackbar.value = false
  if (!('serviceWorker' in navigator)) {
    window.location.reload()
    return
  }
  void activateWaitingWorker(
    navigator.serviceWorker,
    () => window.location.reload(),
    undefined,
    createUpdateRecorder(),
  )
}

function toggleTheme() {
  const newTheme = isDark.value ? 'light' : 'dark'
  theme.global.name.value = newTheme
  localStorage.setItem('theme', newTheme)
  // Save to backend (fire and forget)
  peopleApi.updateMySettings({ theme: newTheme }).catch(() => {})
}

// Load user theme from backend or localStorage
async function loadUserTheme() {
  try {
    // Try localStorage first for immediate response
    const localTheme = localStorage.getItem('theme')
    if (localTheme && ['light', 'dark'].includes(localTheme)) {
      theme.global.name.value = localTheme
    }

    // Then load from backend (authoritative source)
    const response = await peopleApi.getMyProfile()
    const savedTheme = response.data?.settings?.theme as string
    if (savedTheme && ['light', 'dark'].includes(savedTheme)) {
      // Don't override theme on public routes (they manage their own theme via GET params)
      if (!isPublicRoute.value) {
        theme.global.name.value = savedTheme
        localStorage.setItem('theme', savedTheme)
      }
    }
  } catch (e) {
    console.warn('Failed to load user theme:', e)
  }
}

// Global click handler for humanId links rendered via v-html
function handleHumanIdClick(e: MouseEvent) {
  const link = (e.target as HTMLElement).closest('a.human-id-mention[data-human-id]') as HTMLAnchorElement | null
  if (!link) return
  e.preventDefault()
  const href = link.getAttribute('href')
  if (href) router.push(href)
}
document.addEventListener('click', handleHumanIdClick)
onUnmounted(() => document.removeEventListener('click', handleHumanIdClick))

onMounted(async () => {
  // Skip auth initialization for public routes
  // Check both computed (for SPA navigation) and pathname (for initial load before router resolves)
  if (isPublicRoute.value || window.location.pathname.startsWith('/public/')) return

  await authStore.init()
  // Initialize department context (must be before any API calls that need filtering)
  departmentStore.init()
  // Load theme after auth is initialized
  loadUserTheme()
  // Lazy check for push notifications
  checkPushNotifications()
  // Check time tracking admin access (non-blocking)
  timeTrackingApi.adminCheckAccess()
    .then((res) => { hasTimeTrackingAdmin.value = res.data.hasAccess })
    .catch((e) => { console.warn('Time tracking admin access check failed:', e?.response?.status || e?.message) })
})
</script>

<template>
  <v-app>
    <!-- Public routes: minimal layout, no app chrome -->
    <template v-if="isPublicRoute">
      <v-main>
        <router-view />
      </v-main>
    </template>

    <!-- Authenticated routes: full layout -->
    <template v-else>
      <!-- Impersonation Banner -->
      <ImpersonationBanner />

      <v-app-bar
        color="primary"
        density="compact"
      >
        <v-app-bar-nav-icon @click="appStore.toggleDrawer" />

        <v-app-bar-title>
          <span class="font-weight-bold">ВАВТ:ДИТ</span>-KB
        </v-app-bar-title>

        <v-spacer />

        <!-- Department Switcher (superadmin only) -->
        <DepartmentSwitcher v-if="authStore.isSuperAdmin" />

        <v-btn
          icon
          @click="toggleTheme"
        >
          <v-icon>{{ isDark ? 'mdi-white-balance-sunny' : 'mdi-moon-waning-crescent' }}</v-icon>
        </v-btn>

        <!-- Activity Feed Bell -->
        <ActivityFeedBell v-if="authStore.hasAppAccess" />

        <v-menu>
          <template #activator="{ props }">
            <v-btn
              icon
              v-bind="props"
            >
              <v-icon>mdi-account-circle</v-icon>
            </v-btn>
          </template>
          <v-list
            density="compact"
            min-width="200"
          >
            <v-list-item>
              <v-list-item-title class="font-weight-bold">
                {{ authStore.fullName }}
              </v-list-item-title>
              <v-list-item-subtitle>{{ authStore.email }}</v-list-item-subtitle>
            </v-list-item>
            <v-divider />
            <v-list-item
              v-if="authStore.isAdmin"
              prepend-icon="mdi-shield-account"
              title="Администратор"
              disabled
            />
            <v-list-item
              prepend-icon="mdi-logout"
              title="Выйти"
              @click="authStore.signOut"
            />
          </v-list>
        </v-menu>
      </v-app-bar>

      <v-navigation-drawer
        v-model="appStore.drawer"
        temporary
      >
        <v-list
          nav
          density="compact"
        >
          <!-- Show full menu only for users with app roles -->
          <template v-if="authStore.hasAppAccess">
            <!-- Основные разделы -->
            <v-list-item
              prepend-icon="mdi-home"
              title="Главная"
              to="/"
            />
            <v-list-item
              prepend-icon="mdi-text-search"
              title="Поиск"
              to="/search"
            />
            <v-list-item
              prepend-icon="mdi-timer-outline"
              title="Учёт времени"
              to="/time-tracking"
            />
            <v-list-item
              v-if="hasTimeTrackingAdmin"
              prepend-icon="mdi-account-clock"
              title="Контроль времени"
              to="/time-tracking/admin"
            />
            <v-list-item
              prepend-icon="mdi-view-dashboard"
              title="Дашборд"
              to="/dashboard"
            />
            <v-list-item
              prepend-icon="mdi-folder-multiple"
              title="Проекты"
              to="/projects"
            />
            <v-list-item
              prepend-icon="mdi-checkbox-marked-outline"
              title="Задачи"
              to="/tasks"
            />
            <v-list-item
              v-if="authStore.canViewPlanning"
              prepend-icon="mdi-chart-gantt"
              title="Плановый Gantt"
              to="/planning"
            />
            <v-list-item
              prepend-icon="mdi-lightbulb-on-outline"
              title="Идеи"
              to="/ideas"
            />
            <v-list-item
              prepend-icon="mdi-book-open-page-variant"
              title="База знаний"
              to="/knowledge"
            />
            <v-list-item
              prepend-icon="mdi-robot"
              title="AI Ассистент"
              to="/ai"
            />

            <v-list-item
              v-if="canSeeMyReports"
              prepend-icon="mdi-file-account-outline"
              title="Мои отчёты"
              to="/my-reports"
            />

            <v-divider class="my-2" />

            <!-- Отчёты и аналитика (только superadmin) -->
            <!-- Отчёты доступны executive и выше — как и на бэкенде (@Roles executive/admin/superadmin) -->
            <v-list-group
              v-if="authStore.isAdminPlus || authStore.isExecutive"
              value="reports"
            >
              <template #activator="{ props }">
                <v-list-item
                  v-bind="props"
                  prepend-icon="mdi-file-document-outline"
                  title="Отчёты"
                />
              </template>
              <v-list-item
                prepend-icon="mdi-chart-box-outline"
                title="Статистика"
                to="/reports"
              />
              <v-list-item
                prepend-icon="mdi-table-clock"
                title="Табель"
                to="/reports/timesheet"
              />
              <v-list-item
                prepend-icon="mdi-tag-multiple-outline"
                title="Время по типам работ"
                to="/reports/tag-time"
              />
              <v-list-item
                prepend-icon="mdi-chart-areaspline"
                title="Поток задач"
                to="/reports/flow"
              />
              <v-list-item
                prepend-icon="mdi-chart-timeline-variant-shimmer"
                title="Аналитика"
                to="/analytics"
              />
            </v-list-group>
            <v-list-group
              v-if="authStore.hasStrategyAccess"
              value="strategy"
            >
              <template #activator="{ props }">
                <v-list-item
                  v-bind="props"
                  prepend-icon="mdi-strategy"
                  title="Стратегия ЦТ"
                />
              </template>
              <v-list-item
                title="Обзор"
                to="/strategy"
                density="compact"
              />
              <v-list-item
                v-if="authStore.isAdminPlus || authStore.isExecutive"
                title="Классификация"
                to="/strategy/classification"
                density="compact"
              >
                <template
                  v-if="pendingClassifications > 0"
                  #append
                >
                  <v-badge
                    :content="pendingClassifications"
                    color="warning"
                    inline
                  />
                </template>
              </v-list-item>
            </v-list-group>
            <v-list-item
              v-if="authStore.hasStrategyAccess"
              prepend-icon="mdi-radar"
              title="Цифровая зрелость"
              to="/digital-maturity"
            />

            <v-divider class="my-2" />

            <!-- Справочники -->
            <v-list-item
              prepend-icon="mdi-tag-multiple"
              title="Теги"
              to="/tags"
            />
            <v-list-item
              v-if="authStore.isAdminPlus"
              prepend-icon="mdi-account-group"
              title="Сотрудники"
              to="/people"
            />
          </template>

          <!-- Нераспознанные авторы коммитов (admin+) -->
          <v-list-item
            v-if="authStore.isAdminPlus"
            prepend-icon="mdi-account-question"
            title="Авторы коммитов"
            to="/admin/commit-authors"
          />

          <!-- Settings is always visible -->
          <v-list-item
            prepend-icon="mdi-cog"
            title="Настройки"
            to="/settings"
          />

          <!-- Администрирование (только superadmin) -->
          <template v-if="authStore.isSuperAdmin">
            <v-divider class="my-2" />
            <v-list-item
              prepend-icon="mdi-shield-crown"
              title="Администрирование"
              to="/admin"
            />
            <v-list-item
              prepend-icon="mdi-calendar-month"
              title="Производственный календарь"
              to="/admin/work-calendar"
            />
            <v-list-item
              prepend-icon="mdi-broom"
              title="Сборщик задач"
              to="/admin/task-groomer"
            />
          </template>

          <!-- Show message for users without roles -->
          <template v-if="!authStore.hasAppAccess">
            <v-divider class="my-2" />
            <v-list-item>
              <v-list-item-subtitle class="text-wrap">
                У вас нет ролей для доступа к приложению. Обратитесь к администратору.
              </v-list-item-subtitle>
            </v-list-item>
          </template>
        </v-list>
      </v-navigation-drawer>

      <v-main>
        <v-container fluid>
          <router-view />
        </v-container>
      </v-main>

      <!-- Activity Feed Panel -->
      <ActivityFeedPanel v-if="authStore.hasAppAccess" />

      <!-- Global FAB for quick creation -->
      <GlobalFAB v-if="authStore.hasAppAccess" />
    </template>

    <!-- PWA Update Snackbar (hide on public routes) -->
    <v-snackbar
      v-if="!isPublicRoute"
      v-model="showUpdateSnackbar"
      :timeout="-1"
      color="primary"
      location="bottom"
    >
      Доступна новая версия приложения
      <template #actions>
        <v-btn
          variant="text"
          @click="showUpdateSnackbar = false"
        >
          Позже
        </v-btn>
        <v-btn
          variant="flat"
          color="white"
          @click="applyUpdate"
        >
          Обновить
        </v-btn>
      </template>
    </v-snackbar>

    <!-- Push Notifications Prompt (hide on public routes) -->
    <v-snackbar
      v-if="!isPublicRoute"
      v-model="showPushPrompt"
      :timeout="-1"
      color="info"
      location="bottom"
    >
      <v-icon class="mr-2">
        mdi-bell-ring-outline
      </v-icon>
      Включите уведомления, чтобы не пропустить важные события
      <template #actions>
        <v-btn
          variant="text"
          size="small"
          @click="dismissPushPrompt"
        >
          Позже
        </v-btn>
        <v-btn
          variant="flat"
          color="white"
          size="small"
          @click="enablePushNotifications"
        >
          Включить
        </v-btn>
      </template>
    </v-snackbar>

    <!-- Modal Alert Dialog (for testing alerts, status lowered, etc.) -->
    <ModalAlertDialog />
  </v-app>
</template>

<style>
html {
  overflow-y: auto;
}

/* Prevent mobile browser auto-zoom on input focus */
/* Safari/Chrome zoom in if font-size < 16px */
input,
textarea,
[contenteditable] {
  font-size: 16px !important;
}

/* Vuetify v-text-field and v-textarea input elements */
.v-field__input,
.v-field textarea {
  font-size: 16px !important;
}

/* ── HumanId mention badges in rendered markdown ──────────────── */
.human-id-mention {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 1px 8px;
  border-radius: 12px;
  font-size: 0.8125rem;
  font-weight: 500;
  line-height: 1.5;
  white-space: nowrap;
  vertical-align: baseline;
  background: color-mix(in srgb, var(--hid-color) 12%, transparent);
  color: var(--hid-color);
  text-decoration: none;
  cursor: default;
  transition: background 0.15s;
}
a.human-id-mention {
  cursor: pointer;
}
a.human-id-mention:hover {
  background: color-mix(in srgb, var(--hid-color) 22%, transparent);
  text-decoration: none;
}
.human-id-mention .mdi {
  font-size: 14px;
  line-height: 1;
}
</style>
